import { ColumnDef, SortingState } from "@tanstack/react-table";
import { getName } from "country-list";
import React, { useCallback, useMemo } from "react";
import { DataTable } from "../../../components/data-table/DataTable";
import { useLocalization } from "../../../hooks/useLocalization";
import type { UserSessionEntry } from "./types";
import { formatDurationMinutes, resolveUserDisplayName } from "./utils";

export const INITIAL_SORTING: SortingState = [{ id: "date", desc: true }];

const resolveCountryName = (
  code: string | undefined,
  unknownLabel: string,
): string => {
  if (!code || code.toUpperCase() === "ZZ") return unknownLabel;

  return getName(code) ?? code;
};

interface DisplayRow {
  id: string;
  dateDisplay: string;
  dateMs: number;
  user: string;
  experienceName: string;
  durationDisplay: string;
  durationSeconds: number;
  country: string;
  multiplayerSessionId: string;
  _original: UserSessionEntry;
}

/** Format date as "YYYY-MM-DD, HH:mm:ss" in UTC */
const formatSessionDate = (date: Date): string => {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  const hh = String(date.getUTCHours()).padStart(2, "0");
  const mm = String(date.getUTCMinutes()).padStart(2, "0");
  const ss = String(date.getUTCSeconds()).padStart(2, "0");

  return `${y}-${m}-${d}, ${hh}:${mm}:${ss}`;
};

interface UserSessionTableProps {
  data: UserSessionEntry[];
  isLoading?: boolean;
  userDisplayNames?: Map<string, string>;
  onSortingChange?: (sorting: SortingState) => void;
  onFilteredDataChange?: (rows: UserSessionEntry[]) => void;
  filterSlot?: React.ReactNode;
}

export const UserSessionTable: React.FC<UserSessionTableProps> = ({
  data,
  isLoading = false,
  userDisplayNames,
  onSortingChange,
  onFilteredDataChange,
  filterSlot,
}) => {
  const { t } = useLocalization();

  const unknownCountryLabel = t(
    "pages.dataAnalytics.userLevelData.columns.unknownCountry",
  );

  const displayRows = useMemo<DisplayRow[]>(
    () =>
      data.map((entry) => ({
        id: entry.id,
        dateDisplay: formatSessionDate(entry.date),
        dateMs: entry.date.getTime(),
        user: resolveUserDisplayName(entry.userId, userDisplayNames),
        experienceName: entry.experienceName,
        durationDisplay: formatDurationMinutes(entry.durationSeconds),
        durationSeconds: entry.durationSeconds,
        country: resolveCountryName(entry.country, unknownCountryLabel),
        multiplayerSessionId: entry.multiplayerSessionId ?? "",
        _original: entry,
      })),
    [data, userDisplayNames, unknownCountryLabel],
  );

  const columns = useMemo<ColumnDef<DisplayRow, any>[]>(
    () => [
      {
        id: "date",
        header: t("pages.dataAnalytics.userLevelData.columns.date"),
        accessorKey: "dateMs",
        cell: (ctx) => ctx.row.original.dateDisplay,
        enableSorting: true,
        enableColumnFilter: false,
        sortingFn: "basic",
      },
      {
        id: "userId",
        header: t("pages.dataAnalytics.userLevelData.columns.user"),
        accessorKey: "user",
        cell: (ctx) => ctx.getValue<string>(),
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        id: "experienceName",
        header: t("pages.dataAnalytics.userLevelData.columns.experience"),
        accessorKey: "experienceName",
        cell: (ctx) => ctx.getValue<string>(),
        enableSorting: true,
        enableColumnFilter: false,
        meta: { mobileTitle: true },
      },
      {
        id: "duration",
        header: t("pages.dataAnalytics.userLevelData.columns.duration"),
        accessorKey: "durationSeconds",
        cell: (ctx) => ctx.row.original.durationDisplay,
        enableSorting: true,
        enableColumnFilter: false,
        sortingFn: "basic",
      },
      {
        id: "country",
        header: t("pages.dataAnalytics.userLevelData.columns.country"),
        accessorKey: "country",
        cell: (ctx) => ctx.getValue<string>() || "\u2014",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        id: "multiplayerSessionId",
        header: t(
          "pages.dataAnalytics.userLevelData.columns.multiplayerSessionId",
        ),
        accessorKey: "multiplayerSessionId",
        cell: (ctx) => ctx.getValue<string>() || "\u2014",
        enableSorting: true,
        enableColumnFilter: false,
      },
    ],
    [t],
  );

  const getRowId = useCallback((row: DisplayRow) => row.id, []);

  const handleFilteredDataChange = useCallback(
    (rows: DisplayRow[]) => {
      onFilteredDataChange?.(rows.map((r) => r._original));
    },
    [onFilteredDataChange],
  );

  return (
    <DataTable<DisplayRow>
      columns={columns}
      data={displayRows}
      defaultSort={INITIAL_SORTING}
      filterSlot={filterSlot}
      getRowId={getRowId}
      isLoading={isLoading}
      onFilteredDataChange={handleFilteredDataChange}
      onSortingChange={onSortingChange}
      showActionButton={false}
      tableKey="userSessions"
      virtualize={true}
    />
  );
};
